/* eslint-disable jsx-a11y/click-events-have-key-events */
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'antd';
import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import { Droppable } from 'react-beautiful-dnd';

import { PropTypes } from 'prop-types';

import BorderlessButton from '../../common/buttons/BorderlessButton';

import BoardListCard from './BoardListCard';

import {
  getTemplateMap,
} from '../boards.helpers';
import useStatusCards from '../hooks/useStatusCards';

import { openCardDrawer, openFormMapDrawer, openShortListDrawer } from '../state/boards.actions';

const getKey = (statusId) => `board-status-open-state-${statusId}`;
// Local Storage saves as string
const getLocalStorageOpen = (statusId) => window.localStorage.getItem(getKey(statusId)) === 'true';
export default function BoardListSection({
  cardTemplate,
  cards: propCards,
  status: {
    id: statusId,
    title,
    displayFields = [],
  } = {},
  style = {},
  isLocked,
  showCardNumber,
  showListIndex,
  userBoardPermissions,
  projectIdMap,
  onBoardSelected,
  usePropCards,
  boardSortType,
  isDroppable,
  boardId, // used by BoardResultsList for advanced search
}) {
  const droppableId = statusId ? statusId.toString() : 'null';
  const {
    canCreate,
  } = userBoardPermissions;

  const dispatch = useDispatch();

  const {
    statusId: targetStatusId,
  } = useSelector((state) => state.boards.targetCard);
  const allCards = useSelector((state) => state.boards.fullCards);

  const [open, setOpen] = useState(getLocalStorageOpen(statusId));
  const [showColBorder, setShowColBorder] = useState(false);

  const collapseAllTrigger = useSelector((state) => state.boards.collapseAllTrigger);

  const {
    cards,
    shadows,
  } = useStatusCards(statusId);

  const totalCards = cards.length + shadows.length;

  const fullTitle = `${title} ${totalCards}`;
  const cardCount = open ? totalCards : 0;

  const saveOpenState = useCallback((openState) => {
    window.localStorage.setItem(getKey(statusId), openState);
    setOpen(openState);
  }, [statusId]);

  useEffect(() => {
    if (targetStatusId && statusId === targetStatusId) saveOpenState(true);
  }, [targetStatusId, statusId, saveOpenState]);

  useEffect(() => {
    setOpen(getLocalStorageOpen(statusId));
  }, [statusId]);

  useEffect(() => {
    if (!collapseAllTrigger) return;
    const { action } = collapseAllTrigger;
    saveOpenState(action === 'expand');
  }, [collapseAllTrigger, saveOpenState]);

  const onForm = useCallback((cardId) => {
    if (!(cardId in allCards) && !usePropCards) return;
    dispatch(openFormMapDrawer({
      selectedCardId: cardId,
    }));
  }, [dispatch, allCards]);

  const onShortList = useCallback((cardId) => {
    if (!(cardId in allCards) && !usePropCards) return;
    dispatch(openShortListDrawer({
      selectedCardId: cardId,
    }));
  }, [allCards]);

  const onCardClick = useCallback((cardId) => () => {
    dispatch(openCardDrawer({
      selectedCardId: cardId,
      onForm,
      onShortList,
    }));
  }, [dispatch, onForm, onShortList]);

  const onFormClicked = useCallback((cardId) => (e) => {
    e.stopPropagation();
    dispatch(openFormMapDrawer({
      selectedCardId: cardId,
      boardId,
      onForm,
    }));
  }, [dispatch, onForm, boardId]);

  const onShortListClicked = useCallback((cardId) => (e) => {
    e.stopPropagation();
    dispatch(openShortListDrawer({
      selectedCardId: cardId,
    }));
  }, [dispatch]);

  const onAddClicked = useCallback(() => {
    dispatch(openCardDrawer({
      statusId,
    }));
  }, [dispatch, statusId]);

  const onEditClicked = useCallback((cardId) => (e) => {
    e.stopPropagation();
    dispatch(openCardDrawer({
      selectedCardId: cardId,
      editMode: true,
      isLocked,
    }));
  }, [dispatch, isLocked]);

  const onOpenToggle = useCallback(() => {
    saveOpenState(!open);
  }, [open, saveOpenState]);

  const onScroll = useCallback((e) => {
    const {
      target: {
        scrollLeft = 0,
      } = {},
    } = e;
    setShowColBorder(scrollLeft > 10);
  }, []);

  const templateMap = useMemo(() => getTemplateMap(cardTemplate), [cardTemplate]);
  const collapsedMap = useMemo(() => {
    const cMap = {};
    Object.values(templateMap).forEach((sectionMap) => {
      Object.keys(sectionMap).forEach((fieldId) => {
        cMap[fieldId] = sectionMap[fieldId];
      });
    });
    return cMap;
  }, [templateMap]);
  const headers = useMemo(() => {
    const titles = [];
    if (!displayFields) return titles;
    displayFields.forEach((fieldId) => {
      const {
        [fieldId]: {
          configProps: { title: fieldTitle } = {},
        } = {},
      } = collapsedMap;
      if (fieldTitle) {
        titles.push(fieldTitle);
      }
    });
    return titles;
  }, [fullTitle, displayFields, collapsedMap]);

  const filteredDisplayFields = useMemo(() => {
    // Filter out display fields that no longer exist in card template
    if (!displayFields || !cardTemplate) return [];
    const { fields: sections = [] } = cardTemplate;
    const allFieldIds = new Set();
    sections.forEach((section) => {
      const { fields = [] } = section;
      fields.forEach((field) => {
        allFieldIds.add(field.id);
      });
    });
    return displayFields.filter((fieldId) => allFieldIds.has(fieldId));
  }, [displayFields, cardTemplate]);

  const displayMap = useMemo(() => {
    const dMap = {};
    if (filteredDisplayFields.length === 0) return dMap;
    filteredDisplayFields.forEach((fieldId, displayIdx) => {
      dMap[fieldId] = displayIdx;
    });
    return dMap;
  }, [filteredDisplayFields]);

  const getRowComponent = useCallback((card, idx) => (
    <BoardListCard
      key={card.id}
      idx={idx}
      cardId={card.id}
      templateMap={templateMap}
      displayFields={displayFields}
      displayMap={displayMap}
      onCardClick={onCardClick}
      onEditClicked={onEditClicked}
      onFormClicked={onFormClicked}
      onShortListClicked={onShortListClicked}
      isLocked={isLocked}
      showCardNumber={showCardNumber}
      showListIndex={showListIndex}
      userBoardPermissions={userBoardPermissions}
      projectIdMap={projectIdMap}
      onBoardSelected={onBoardSelected}
      usePropCard={usePropCards}
      boardSortType={boardSortType}
      {// eslint-disable-line react/jsx-props-no-spreading
        ...usePropCards
          ? { card }
          : {}
      }
    />
  ), [
    templateMap,
    displayFields,
    displayMap,
    isLocked,
    onCardClick,
    onEditClicked,
    onShortListClicked,
    userBoardPermissions,
    projectIdMap,
    onBoardSelected,
    usePropCards,
    boardSortType,
  ]);

  const fullCards = useMemo(() => (
    (usePropCards ? propCards : cards).map(getRowComponent)
  ), [
    cards,
    usePropCards,
    propCards,
    getRowComponent,
  ]);

  const fullShadows = useMemo(() => (
    shadows.map(getRowComponent)
  ), [
    shadows,
    getRowComponent,
  ]);

  const addButton = (
    <BorderlessButton
      iconNode={<PlusOutlined />}
      onClick={onAddClicked}
      style={{
        width: 26, backgroundColor: 'transparent', height: 26, fontSize: 18, padding: 0,
      }}
    />
  );

  return (
    <div style={{ position: 'relative', ...style }}>
      <div className="board-list-view-status-section" onScroll={onScroll}>
        <table className="board-list-table">
          <thead>
            <tr
              className="boards-column-header"
              style={cardCount === 0 ? { borderBottom: 'none' } : {}}
            >
              <th
                className="board-list-title-column-header"
                style={{ width: 300, paddingLeft: 5 }}
              >
                <Row align="middle" gutter={5}>
                  <Col>
                    <button
                      style={{
                        background: 'none',
                        color: 'inherit',
                        border: 'none',
                        padding: 0,
                        font: 'inherit',
                        outline: 'inherit',
                        cursor: 'pointer',
                        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
                      }}
                      onClick={onOpenToggle}
                      aria-label="expand"
                      type="button"
                    >
                      <DownOutlined />
                    </button>
                  </Col>
                  <Col>
                    {fullTitle}
                  </Col>
                  {
                    canCreate
                      && (
                      <Col>
                        {addButton}
                      </Col>
                      )
                  }
                </Row>
              </th>
              {headers.map((h, i) => <th key={i} className="board-list-header">{h}</th>)}
            </tr>
          </thead>
          <Droppable droppableId={droppableId} type="CARDS" direction="vertical" isDropDisabled={!isDroppable}>
            {({ droppableProps, innerRef, placeholder }) => (
              <tbody
                {...droppableProps}
                ref={innerRef}
                style={
                  (fullCards.length === 0 && fullShadows.length === 0) || !open
                    ? { height: 1 }
                    : {}
                }
              >
                {open ? fullCards : []}
                {open ? fullShadows : []}
                {placeholder}
              </tbody>
            )}
          </Droppable>
        </table>
      </div>
      {
        showColBorder
        && <div className="board-list-title-column-border" />
      }
    </div>
  );
}

BoardListSection.propTypes = {
  isLocked: PropTypes.number,
  style: PropTypes.shape({
    width: PropTypes.string,
  }),
  cards: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    orderIndex: PropTypes.number,
    onClick: PropTypes.func,
    onEdit: PropTypes.func,
    onForm: PropTypes.func,
    onShortList: PropTypes.func,
    isLocked: PropTypes.number,
    color: PropTypes.string,
    users: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      avatar: PropTypes.string,
    })),
    draggable: PropTypes.bool,
    data: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      type: PropTypes.string,
      value: PropTypes.string,
    })),
  })),
  displayFields: PropTypes.arrayOf(PropTypes.string),
  cardTemplate: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    sections: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.string,
      title: PropTypes.string,
      fields: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        type: PropTypes.string,
        value: PropTypes.string,
      })),
    })),
    fields: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  status: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    displayFields: PropTypes.arrayOf(PropTypes.string),
  }),
  userBoardPermissions: PropTypes.shape({
    canCreate: PropTypes.bool,
  }),
  showCardNumber: PropTypes.bool,
  showListIndex: PropTypes.bool,
  projectIdMap: PropTypes.shape({}),
  onBoardSelected: PropTypes.func,
  usePropCards: PropTypes.bool,
  boardSortType: PropTypes.string,
  isDroppable: PropTypes.bool,
  boardId: PropTypes.string,
};

BoardListSection.defaultProps = {
  isLocked: 0,
  style: {},
  cards: [],
  displayFields: [],
  cardTemplate: {},
  userBoardPermissions: {
    canCreate: false,
  },
  showCardNumber: false,
  showListIndex: false,
  status: {},
  projectIdMap: {},
  onBoardSelected: undefined,
  usePropCards: false,
  boardSortType: 'default',
  isDroppable: true,
  boardId: null,
};
